// TODO:
// In order to keep TariffChart working?
// moment should be replaced once all components accept moment instead of date-fns
// at this moment all formatting use Moment on e3dc side

import React, { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react'
import { Area, CartesianGrid, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, Customized } from 'recharts'
import { CategoricalChartFunc } from 'recharts/types/chart/generateCategoricalChart'
import { formatYTicks } from 'src/Libs/dynamic-power-prices/utils'
import { useXAxisTicks } from 'src/Libs/chart-kit'
import { TariffChartTooltip } from './TariffChartTooltip'
import moment from '@e3dc-react/shell/Libs/moment'
import { sortBy } from 'lodash'
import { TariffModel } from '../../../models/tariffs-model'
import { useTheme } from '@mui/material'
import { HourSegmentIndicator } from './HourSegmentIndicator'
import { TariffDetailsModel } from '../../../models/single-tariff-model'
import { ChartRefType } from '../../../../shared/types/chart-ref-type'
import { getCurrencyValue } from '../../../../shared/utils/currency-value'
import { getSubunitOfCurrency } from '../../../utils/get-subunit-of-currency'
import { addHours, format, toZonedTime } from '@e3dc-react/shell/Libs/date'

interface TariffChartProps {
  tariff: TariffDetailsModel
  height?: number
}

/**
 * Dynamic tariffs chart component. Since dynamic tariffs specify a unique value for each hour of the day, this
 * chart represents each hour as a horizontal line segment and emphasize it by thickening this line and showing
 * dots at the edges of the line segment. To support this kind of user experience, a few workarounds were made
 * against the default Recharts components:
 *
 * By default, AreaChart is fed with points and tooltip is activated when hovered over the point between two
 * line segments. This is not what the data represents and hovering must be enabled for the whole line segment,
 * which Area does not support. To work around this, a listener to user's pointer position is implemented and
 * does the following:
 *
 * - read user's horizontal pointer position "x"
 * - read all points from the chart and find the index of segment inside which user's pointer is
 * - calculate how many pixels two points are away one from another
 * - store all this info in a ref to prevent unneeded renders
 *
 * Then, use active segment index to:
 * - pick which price from the source tariff should be shown in the hover tooltip
 * - render an "active segment" over the chart(width of this segment is equal to the "gap")
 *
 * NOTE: TypeScript definitions from Recharts are nested and using generic types difficult to specify when overriding
 * default behavior. For this reason, Props interfaces for custom chart components are manually typed after inspecting
 * the props object in runtime.
 *
 * @param - single tariff as TariffDetailsModel
 */
export const TariffChart: React.FC<TariffChartProps> = ({ tariff, height }) => {
  const theme = useTheme()
  const mousePositionRef = useRef<{ x: number; horizontalGap: number; activeSegmentIndex: number } | null>(null)
  /**
   * chart ref types not supported yet by Recharts https://github.com/recharts/recharts/issues/2665
   * We manually define necessary fields by inspecting the ref object in runtime.
   * */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartRef = useRef<any>(null)

  const { multiplier, subunit } = getSubunitOfCurrency(tariff.currencyCode)

  const pricesAdjustedForSubunit = tariff.dynamicPrices.map(price => ({
    gridPrice: price.gridPrice * multiplier,
    solarPrice: price.solarPrice * multiplier,
    startDateTime: price.startDateTime,
    endDateTime: price.endDateTime,
  }))
  const sortedPrices = sortBy(pricesAdjustedForSubunit, price => new Date(price.startDateTime || ''))

  const [earliestPriceTimeslot] = useState(() => moment(sortedPrices[0].startDateTime))
  const [latestPriceTimeslot] = useState(() => moment(sortedPrices[sortedPrices.length - 1].startDateTime))

  const { ticks, format: xAxisTicksFormat } = useXAxisTicks(earliestPriceTimeslot, latestPriceTimeslot, 'hour')
  const xAxisTicks = ticks.map(tick => toZonedTime(tick, 'UTC').toISOString())

  const data = useMemo(() => {
    if (tariff.dynamicPrices.length === 0) {
      return []
    }

    const lastPrice = sortedPrices[sortedPrices.length - 1]

    /** extend the chart by one hour to avoid the last point being at the very end of the chart */
    const priceExtension = {
      ...lastPrice,
      startDateTime: addHours(new Date(lastPrice.startDateTime ?? 0), 1).toISOString(),
    }

    const extendedData = [...sortedPrices, priceExtension]

    return extendedData
  }, [tariff, sortedPrices])

  const formatYAxisTicks = useCallback(
    (value: number) => {
      if (subunit) {
        return formatYTicks(value.toString(), subunit)
      }

      return getCurrencyValue(value, tariff.currencyCode)
    },
    [tariff, subunit]
  )

  const handleMouseMove: CategoricalChartFunc = useCallback(event => {
    const e = event as unknown as { chartX: number }
    /**
     * Recharts stores each point as "graphical item" that contains both input data AND calculated pixel
     * positions for each point. Input data is used to fill the labels with text, pixel data to position
     * those labels absolutely in the chart.
     **/
    const graphicalItems = (chartRef as MutableRefObject<ChartRefType<TariffModel['dynamicPrices'][0]>>).current?.state?.formattedGraphicalItems[0]
    const points = graphicalItems?.props?.points || []

    if (e.chartX === undefined || points.length === 0) {
      /** chart empty or pointer invalid, no need to render anything, so we dan immediately return */
      return
    }

    /** since segments are evenly spaced, we can calculate pixel width of one segment at current screen size */
    const horizontalGap = points[0] && points[1] ? points[1].x - points[0].x : 0

    const activeSegmentIndex = graphicalItems.props.points.findIndex(point => {
      return point.x + horizontalGap >= e.chartX
    })

    mousePositionRef.current = {
      x: e.chartX,
      horizontalGap,
      activeSegmentIndex,
    }
  }, [])

  return (
    <ResponsiveContainer height={height || 350} width={'100%'}>
      <ComposedChart
        data={data}
        onMouseEnter={handleMouseMove}
        onMouseMove={handleMouseMove}
        ref={chartRef}
        margin={{ top: 16, bottom: 0, left: 0, right: 0 }}
      >
        <defs>
          <linearGradient id="chartColor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.palette.chart.dynamicTariffChartAreaBackgroundColor} stopOpacity={0.8} />
            <stop offset="100%" stopColor={theme.palette.chart.dynamicTariffChartAreaBackgroundColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray={'3 3'} stroke={theme.palette.grey.grey05} opacity={0.3} vertical={false} />
        <Tooltip
          content={props => (
            <TariffChartTooltip
              {...props}
              priceComponent={
                mousePositionRef.current?.activeSegmentIndex !== undefined ? data[mousePositionRef.current.activeSegmentIndex] : undefined
              }
              currencyCode={tariff.currencyCode}
            />
          )}
          cursor={false}
          offset={30}
          filterNull
        />

        <Area
          dot={false}
          type="stepAfter"
          dataKey="gridPrice"
          stroke={theme.palette.chart.dynamicTariffChartAreaStrokeColor}
          fillOpacity={1}
          fill="url(#chartColor)"
          isAnimationActive={false}
          width={100}
          activeDot={false}
        />

        <Customized
          /**
           * "props" are a subinterface of CustomizedProps exported from Recharts. However, the return type of "component" prop
           * is incorrectly specified as ReactElement instead of React FunctionComponent that accepts props from parent. For this
           * reason, lint message for this props must be suppressed
           */
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          component={(props: any) => (
            <HourSegmentIndicator
              {...props}
              horizontalGap={mousePositionRef.current?.horizontalGap || 0}
              activeSegmentIndex={mousePositionRef.current?.activeSegmentIndex}
            />
          )}
        />

        <YAxis
          orientation="right"
          mirror
          strokeWidth={0}
          dataKey="gridPrice"
          allowDataOverflow
          fontSize={theme.typography.caption.fontSize}
          tick={{
            dy: -8,
            stroke: theme.palette.grey.grey00,
            strokeWidth: 1,
            strokeOpacity: 0.3,
          }}
          tickFormatter={formatYAxisTicks}
        />
        <XAxis
          dataKey="startDateTime"
          ticks={xAxisTicks}
          fontSize={theme.typography.caption.fontSize}
          fontWeight={700}
          tickFormatter={value => format(value, xAxisTicksFormat)}
          scale="point"
          tick={{ stroke: theme.palette.grey.grey00, strokeWidth: 1, strokeOpacity: 0.1 }}
        />
        <ReferenceLine y={0} strokeWidth={1} stroke={theme.palette.grey.A100} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
